import React, { FC, memo, useCallback, useEffect, useReducer } from 'react';

import { Box, HStack, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';

import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { AddButton } from 'components/DataEntry';
import { PolicyIcon } from 'components/Icons';
import { PoliciesTableHeader } from 'containers/PolicyHub/Components/PoliciesTableHeader/PoliciesTableHeader';
import { policyTypeOptions } from 'containers/PolicyHub/Components/PoliciesTableHeader/utils';
import { PoliciesTable } from 'containers/PolicyHub/Policies/PoliciesTable';
import { selectPolicyHub } from 'containers/PolicyHub/selectors';
import { actions } from 'containers/PolicyHub/slice';
import { selectSharedState } from 'containers/SharedState/selectors';
import { useSearchParams } from 'hooks/useSearchParams';

export const Policies: FC = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { policies } = useSelector(selectPolicyHub);
  const { isDevOrTest } = useSelector(selectSharedState);

  const { category } = useSearchParams();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      resourceType: {},
      alertCategory: {},
      currentRecord: {},
      searchTerm: '',
      severity: { label: 'All Severities', value: undefined },
      policyType: policyTypeOptions[0],
    },
  );

  const getPolicies = useCallback(() => {
    dispatch(
      actions.getPolicies({
        q: {
          alertCategory: state.alertCategory?.value || undefined,
          resourceType: state.resourceType?.value || undefined,
          severity: state.severity?.value || undefined,
          page: policies.page.info.page_number,
          size: policies.page.info.page_size,
          searchTerm: !!state.searchTerm ? state.searchTerm : undefined,
          isCustom:
            state.policyType?.value === 'custom'
              ? true
              : state.policyType?.value === 'system'
              ? false
              : undefined,
        },
      }),
    );
  }, [
    dispatch,
    state.searchTerm,
    state.alertCategory,
    state.resourceType,
    policies.page.info,
    state.policyType,
    state.severity,
  ]);

  // get policies
  useDeepCompareEffect(() => {
    getPolicies();
  }, [getPolicies]);

  useEffect(() => {
    // The filter is read as an option (alertCategory.value), so wrap the URL value.
    if (!!category)
      updateState({ alertCategory: { label: category, value: category } });
  }, [category]);

  const updateValue = useCallback((field, value) => {
    updateState({ [field]: value });
  }, []);

  return (
    <Card styles={{ card: { w: 'full', h: 'full', flex: 1 } }}>
      <Stack w="full" h="full" spacing={4}>
        <HStack w="full" justify="space-between">
          <Box>
            <PageHeaderWithIcon
              label={'Policies'}
              reversed
              icon={<PolicyIcon />}
            />
          </Box>
          {isDevOrTest && (
            <Box>
              <AddButton
                onClick={() => navigate('/policy-hub/policy/cloud/add')}
                label={'Create Policy'}
              />
            </Box>
          )}
        </HStack>
        <PoliciesTableHeader
          resourceType={state.resourceType}
          alertCategory={state.alertCategory}
          policyType={state.policyType}
          severity={state.severity}
          updateValue={updateValue}
          action="Show"
        />
        <PoliciesTable />
      </Stack>
    </Card>
  );
});
